import { CheckCircle, XCircle, RefreshCw, Lightbulb } from 'lucide-react';
import { Button } from '../ui/button';
import EmailCaptureForm from './EmailCaptureForm';

function getStatusBadge(score) {
  if (score >= 67) return { text: 'Compatible', bg: 'bg-[#27AE60]/10', color: 'text-[#27AE60]' };
  if (score >= 34) return { text: 'Partial', bg: 'bg-yellow-100', color: 'text-yellow-700' };
  return { text: 'Not Compatible', bg: 'bg-red-100', color: 'text-red-600' };
}

function ProtocolSection({ title, result }) {
  const status = getStatusBadge(result.score);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6 card-hover">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <h3 className="font-['Montserrat'] font-bold text-lg text-[#2C3E50]">{title}</h3>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.bg} ${status.color}`}>
          {status.text} &mdash; {result.score}%
        </span>
      </div>

      <div className="space-y-2 mb-4">
        {Object.entries(result.checks).map(([key, value]) => (
          <div key={key} className="flex items-center gap-2">
            {value
              ? <CheckCircle size={16} className="text-[#27AE60] shrink-0" />
              : <XCircle size={16} className="text-red-500 shrink-0" />
            }
            <span className="text-sm text-[#64748B] capitalize">
              {key.replace(/_/g, ' ')}
            </span>
          </div>
        ))}
      </div>

      {result.gaps.length > 0 && (
        <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-100">
          <p className="text-sm font-semibold text-[#2C3E50] mb-2">Gaps to address:</p>
          <ul className="space-y-1">
            {result.gaps.map((gap, i) => (
              <li key={i} className="text-sm text-[#64748B] flex items-start gap-2">
                <span className="text-yellow-500 mt-0.5">•</span>
                {gap}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CheckerResults({ results, onCheckAnother }) {
  return (
    <div className="max-w-3xl mx-auto">
      {/* Store Info */}
      <div className="bg-[#F8F9FA] rounded-xl p-5 mb-8 border border-slate-200">
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#64748B] mb-1">
          Checked Store
        </p>
        <p className="font-['Montserrat'] font-bold text-[#2C3E50]">{results.url}</p>
        <p className="text-sm text-[#64748B] mt-1">
          Platform detected:{' '}
          <span className="font-medium text-[#3498DB] capitalize">{results.platform}</span>
        </p>
      </div>

      {/* Protocol Results */}
      <ProtocolSection
        title="OpenAI Agentic Commerce Protocol (ACP)"
        result={results.acp}
      />
      <ProtocolSection
        title="Google Universal Commerce Protocol (UCP)"
        result={results.ucp}
      />

      {/* Recommendations */}
      {results.recommendations.length > 0 && (
        <div className="bg-[#3498DB]/5 border border-[#3498DB]/20 rounded-xl p-6 mb-6">
          <h3 className="font-['Montserrat'] font-bold text-[#2C3E50] mb-4 flex items-center gap-2">
            <Lightbulb size={18} className="text-[#3498DB]" />
            Top Recommendations
          </h3>
          <ol className="space-y-3">
            {results.recommendations.map((rec, i) => (
              <li key={i} className="flex gap-3 text-sm text-[#64748B]">
                <span className="font-['Montserrat'] font-bold text-[#3498DB] shrink-0">{i + 1}.</span>
                <span>{rec}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Email Capture */}
      <EmailCaptureForm checkId={results.id} />

      {/* Check Another */}
      <div className="text-center mt-8">
        <button
          onClick={onCheckAnother}
          className="inline-flex items-center gap-2 text-sm text-[#3498DB] hover:text-[#2980B9] font-medium transition-colors duration-200"
        >
          <RefreshCw size={14} />
          Check another store
        </button>
      </div>
    </div>
  );
}
